import { createContext, useContext, useEffect, useState } from "react";
import { config } from "@/config/env";

type Theme = "dark" | "light" | "system";

/**
 * Brand axis for the runtime brand preview (BRAND_TOGGLE_PLAN).
 * "task" is the default Task brand; "brandb" applies the alternate
 * design-system preview via the `data-brand` attribute on <html>.
 * The key is deliberately neutral — no third-party names in client code.
 */
type Brand = "task" | "brandb";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  brandStorageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  brand: Brand;
  setBrand: (brand: Brand) => void;
};

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
  brand: "task",
  setBrand: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

function readStoredBrand(storageKey: string): Brand {
  // The preview is env-gated: with the flag off, a stale localStorage
  // value must not activate the alternate brand.
  if (!config.brandPreviewEnabled) return "task";
  const stored = localStorage.getItem(storageKey);
  return stored === "brandb" ? "brandb" : "task";
}

export function ThemeProvider({
  children,
  defaultTheme = "light", // Light-first (Parchment) as per Task brand guide
  storageKey = "platform-theme",
  brandStorageKey = "platform-brand",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme,
  );
  const [brand, setBrand] = useState<Brand>(() =>
    readStoredBrand(brandStorageKey),
  );

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove("light", "dark");

    // Brand B's guide is light-only: while the preview is active the app
    // is forced light regardless of the stored mode. The stored mode is
    // preserved so switching back to Task restores it.
    if (brand === "brandb") {
      root.setAttribute("data-brand", "brandb");
      root.classList.add("light");
      return;
    }
    root.removeAttribute("data-brand");

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";

      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme, brand]);

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
    brand,
    setBrand: (next: Brand) => {
      if (!config.brandPreviewEnabled) return;
      localStorage.setItem(brandStorageKey, next);
      setBrand(next);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
